import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import type { ArchetypeWithSubarchetypes } from "@liga/shared";
import { api } from "../api/client";
import type {
  AdminDeckDecklistRow,
  AdminDeckDecklistsResponse,
} from "../api/client";
import { ErrorBox, Loading } from "./Loading";
import { SubarchetypeSelect } from "./DecksAdminPanel";

/**
 * Per-event_result classifier for every list registered under a given
 * deck name. Classification lives exclusively on
 * `event_results.subarchetype_id` now — there's no deck-level default to
 * fall back to — so every row owns its own subarchetype pick.
 */
export function DeckDecklistsAdminPanel() {
  const params = useParams<{ deckId: string }>();
  const deckId = Number(params.deckId);
  const queryClient = useQueryClient();

  const decklistsQuery = useQuery<AdminDeckDecklistsResponse>({
    queryKey: ["admin", "decks", deckId, "decklists"],
    queryFn: () => api.adminDeckDecklists(deckId),
    enabled: Number.isFinite(deckId),
  });

  const archetypesQuery = useQuery<ArchetypeWithSubarchetypes[]>({
    queryKey: ["admin", "archetypes"],
    queryFn: api.adminArchetypes,
  });

  const classifyMutation = useMutation({
    mutationFn: (input: {
      eventResultId: number;
      subarchetypeId: number | null;
    }) =>
      api.adminSetEventResultSubarchetype(input.eventResultId, {
        subarchetypeId: input.subarchetypeId,
      }),
    onSuccess: () => {
      // The classification fans out to many downstream reads. Invalidate
      // everything that surfaces a per-event_result subarchetype so the
      // change is reflected without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: ["admin", "decks", deckId, "decklists"],
      });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["matches"] });
      queryClient.invalidateQueries({ queryKey: ["metagame"] });
      queryClient.invalidateQueries({ queryKey: ["subarchetype"] });
    },
  });

  if (decklistsQuery.isLoading) {
    return <Loading label="Carregando listas do deck..." />;
  }
  if (decklistsQuery.error) {
    const err = decklistsQuery.error as Error;
    if (/404/.test(err.message)) {
      return (
        <section className="rounded-2xl border border-line bg-white p-6 space-y-3">
          <Link
            to="/theadmin/decks"
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            ← Voltar para decks
          </Link>
          <h2 className="font-display text-xl font-bold text-ink">
            Deck não encontrado
          </h2>
          <p className="text-sm text-[#6b6356]">
            O deck #{deckId} não existe (talvez tenha sido apagado).
          </p>
        </section>
      );
    }
    return <ErrorBox error={err} />;
  }
  if (!decklistsQuery.data) {
    return <ErrorBox error={new Error("Deck not found")} />;
  }

  const { deck, decklists } = decklistsQuery.data;
  const archetypes = archetypesQuery.data ?? [];
  const withCards = decklists.filter((d) => d.hasDecklist);
  const withoutCards = decklists.filter((d) => !d.hasDecklist);
  const unclassified = decklists.filter((d) => d.subarchetypeId === null).length;

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-5">
      <header className="space-y-2">
        <Link
          to="/theadmin/decks"
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          ← Voltar para decks
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
              Listas do deck
            </div>
            <h2 className="font-display text-xl font-bold text-ink">
              {deck.name}
            </h2>
            <p className="text-sm text-[#6b6356] mt-1">
              Cada lista abaixo recebe seu próprio subarquétipo.{" "}
              {unclassified > 0 ? (
                <span className="font-semibold text-ink">
                  {unclassified} lista(s) sem classificação.
                </span>
              ) : (
                <span className="italic text-[#9a9182]">
                  Todas as listas estão classificadas.
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8a8170] font-semibold">
              Total de usos
            </div>
            <div className="font-display text-2xl font-bold text-ink tabular-nums">
              {decklists.length}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#a39a87]">
              {withCards.length} com cartas
            </div>
          </div>
        </div>
      </header>

      {decklists.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line bg-[#fbfaf6] px-4 py-6 text-center text-sm text-[#6b6356]">
          Esse deck ainda não foi usado em nenhum evento.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2 font-bold">Evento</th>
                <th className="text-left px-3 py-2 font-bold">Data</th>
                <th className="text-left px-3 py-2 font-bold">Jogador</th>
                <th className="text-right px-3 py-2 w-16 font-bold">#</th>
                <th className="text-right px-3 py-2 w-20 font-bold">Pontos</th>
                <th className="text-left px-3 py-2 w-72 font-bold">
                  Subarquétipo
                </th>
                <th className="text-left px-3 py-2 w-40 font-bold">Lista</th>
              </tr>
            </thead>
            <tbody>
              {decklists.map((row) => (
                <DecklistRow
                  key={row.eventResultId}
                  row={row}
                  archetypes={archetypes}
                  saving={
                    classifyMutation.isPending &&
                    classifyMutation.variables?.eventResultId ===
                      row.eventResultId
                  }
                  onChange={(subarchetypeId) =>
                    classifyMutation.mutate({
                      eventResultId: row.eventResultId,
                      subarchetypeId,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {withoutCards.length > 0 && withCards.length > 0 && (
        <p className="text-xs text-[#8a8170]">
          {withoutCards.length} lista(s) não têm snapshot — esses resultados
          vieram da planilha antiga ou de uma importação sem `meleeDecklistId`.
        </p>
      )}
    </section>
  );
}

interface DecklistRowProps {
  row: AdminDeckDecklistRow;
  archetypes: ArchetypeWithSubarchetypes[];
  saving: boolean;
  onChange: (subarchetypeId: number | null) => void;
}

function DecklistRow({ row, archetypes, saving, onChange }: DecklistRowProps) {
  return (
    <tr className="border-t border-[#f3efe6] hover:bg-[#fbfaf6] transition-colors align-top">
      <td className="px-3 py-2.5 text-ink">{row.eventName}</td>
      <td className="px-3 py-2.5 tabular-nums text-[#6b6356]">
        {row.eventDate}
      </td>
      <td className="px-3 py-2.5 text-[#3f3a31]">{row.playerName}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[#6b6356]">
        {row.rank}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-ink">
        {row.points}
      </td>
      <td className="px-3 py-2.5">
        <SubarchetypeSelect
          archetypes={archetypes}
          value={row.subarchetypeId}
          onChange={onChange}
          disabled={saving}
          ariaLabel={`Subarquétipo para ${row.playerName} em ${row.eventName}`}
        />
      </td>
      <td className="px-3 py-2.5">
        {row.hasDecklist ? (
          <Link
            to={`/events/${row.eventId}/decks/${row.eventResultId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-700 font-semibold hover:underline decoration-dotted underline-offset-2"
          >
            Abrir lista ↗
          </Link>
        ) : row.meleeDecklistId ? (
          <a
            href={`https://melee.gg/Decklist/View/${row.meleeDecklistId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#6b6356] hover:text-brand-700 hover:underline decoration-dotted underline-offset-2 text-xs"
          >
            Ver no melee.gg ↗
          </a>
        ) : (
          <span className="italic text-[#b8b0a0] text-xs">sem snapshot</span>
        )}
      </td>
    </tr>
  );
}
